/**
 * Sentry — initialisation + helpers pour cinqo-ops.
 *
 * Le DSN vient de la variable d env VITE_SENTRY_DSN. Si elle n est pas
 * definie (dev local), Sentry ne s initialise pas — pas de bruit inutile.
 *
 * On active :
 *   - BrowserTracing (perf / navigation React Router)
 *   - Replay (session replay sur crash uniquement, pas en continu)
 *   - ErrorBoundary React via l export `SentryErrorBoundary`
 *
 * Le Vite plugin (`@sentry/vite-plugin`) est configure dans vite.config.ts
 * pour uploader les source maps en CI.
 */
import * as Sentry from '@sentry/react'

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined

// Sentry environment :
// 1. VITE_SENTRY_ENVIRONMENT (explicite via env var CF Pages) → priorité.
//    Permet de distinguer 'staging' de 'production' meme quand le build mode
//    Vite est 'production' dans les deux cas (vite build = mode production
//    par defaut, qu'on soit sur prod.cinqo.fr ou staging.ops.cinqo.fr).
// 2. Sinon fallback sur import.meta.env.MODE (dev local = 'development').
const ENV =
  (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
  import.meta.env.MODE

let _initialized = false

export function initSentry() {
  if (_initialized) return
  if (!DSN) {
    console.log('[sentry] pas de DSN — skip init')
    return
  }
  // Dev local (vite dev) : ne rien remonter. Evite le bruit des fantomes HMR
  // (une variable renommee/supprimee sous serveur chaud emet un ReferenceError
  // transitoire en environment=development). Staging/prod sont des `vite build`
  // (import.meta.env.DEV = false) et gardent Sentry.
  if (import.meta.env.DEV) {
    console.log('[sentry] dev local — skip init (pas de remontee)')
    return
  }

  Sentry.init({
    dsn: DSN,
    environment: ENV,

    // Version liee au build (a enrichir en CI avec VITE_SENTRY_RELEASE)
    release: (import.meta.env.VITE_SENTRY_RELEASE as string) || undefined,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Pas de replay continu — uniquement sur erreur
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Perf : sample 20% du trafic en prod, 100% en dev
    tracesSampleRate: ENV === 'production' ? 0.2 : 1.0,

    // Replay : 0% en continu, 100% sur erreur
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    // Ne pas envoyer les PII (emails, IPs) — RGPD
    sendDefaultPii: false,

    // Ignorer le bruit courant
    ignoreErrors: [
      // Extensions navigateur
      /^Non-Error promise rejection captured/,
      /ResizeObserver loop/,
      // Abort de fetch (navigation rapide)
      'AbortError',
      'The operation was aborted',
      // Supabase auth token refresh en background
      'AuthRetryableFetchError',
    ],
  })

  _initialized = true
  console.log('[sentry] init OK env=', ENV)
}

/** Identifier l utilisateur connecte (post-login) */
export function setSentryUser(user: { id: string; email?: string | null }) {
  Sentry.setUser({ id: user.id, email: user.email ?? undefined })
}

/** Deconnecter l utilisateur (post-logout) */
export function clearSentryUser() {
  Sentry.setUser(null)
}

/** ErrorBoundary React exportee pour wrapper l app */
export const SentryErrorBoundary = Sentry.ErrorBoundary
